<script>
import { ref } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useAlert, useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import MessageApi from 'dashboard/api/inbox/message.js';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

const MODEL_OPTIONS = [
  {
    value: 'auto',
    label: 'Auto',
    hint: 'Сам выберет, где Sonnet, а где уже нужен Opus.',
  },
  {
    value: 'sonnet',
    label: 'Sonnet',
    hint: 'Быстрее. Хорошо для FAQ и обычных уточнений.',
  },
  {
    value: 'opus',
    label: 'Opus',
    hint: 'Тяжелая артиллерия для сложных и мутных кейсов.',
  },
];

const EXECUTION_MODE_OPTIONS = [
  {
    value: 'draft',
    label: 'Черновик',
    hint: 'Агент только соберёт ответ и рекомендации. Без live-изменений в бронях.',
  },
  {
    value: 'operator_command',
    label: 'Операторская команда',
    hint: 'Если фактов хватает, агент может сразу создать, изменить, отменить, восстановить бронь или перевести её в нужный статус без второго approve.',
  },
];

function prepareAssistantDraftForRichEditor(content) {
  return String(content || '')
    .trim()
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => {
      const trimmedRight = line.replace(/[ \t]+$/u, '');
      return trimmedRight ? `${trimmedRight}  ` : '';
    })
    .join('\n');
}

export default {
  name: 'ReplyTopPanel',
  components: {
    Dialog,
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
  },
  emits: ['setReplyMode', 'togglePopout', 'executeCopilotAction'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { captainTasksEnabled } = useCaptain();
    const showCopilotMenu = ref(false);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: true,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      toggleCopilotMenu,
      handleClickOutside,
    };
  },
  data() {
    return {
      agentPrompt: '',
      requestedModel: 'auto',
      assistantExecutionMode: 'draft',
      assistantDraft: '',
      assistantError: '',
      assistantStatus: '',
      activeAssistantRequestId: '',
      activeTriggerMessageId: '',
      isAssistantDialogOpen: false,
      isAssistantRunning: false,
      isAlive: true,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
    assistantModelOptions() {
      return MODEL_OPTIONS;
    },
    selectedModelHint() {
      return (
        this.assistantModelOptions.find(
          option => option.value === this.requestedModel
        )?.hint || ''
      );
    },
    assistantExecutionModeOptions() {
      return EXECUTION_MODE_OPTIONS;
    },
    selectedExecutionModeHint() {
      return (
        this.assistantExecutionModeOptions.find(
          option => option.value === this.assistantExecutionMode
        )?.hint || ''
      );
    },
    isOperatorCommandMode() {
      return this.assistantExecutionMode === 'operator_command';
    },
    assistantRunButtonLabel() {
      return this.isOperatorCommandMode
        ? 'Применить и собрать ответ'
        : 'Запустить агента';
    },
    canRunAssistant() {
      return !this.disabled && !!this.conversationId && !this.isAssistantRunning;
    },
    canInsertAssistantDraft() {
      return !this.isAssistantRunning && !!this.assistantDraft;
    },
    selectedChatMessages() {
      const messages = this.$store.getters.getSelectedChat?.messages;
      return Array.isArray(messages) ? messages : [];
    },
    assistantButtonClass() {
      return {
        'text-n-blue-11 hover:enabled:!bg-n-blue-3': !this.isAssistantDialogOpen,
        'text-n-blue-11 bg-n-blue-3': this.isAssistantDialogOpen,
      };
    },
  },
  watch: {
    conversationId() {
      this.resetAssistantState();
      this.requestedModel = 'auto';
      this.assistantExecutionMode = 'draft';
      this.agentPrompt = '';
      this.dismissAssistantDialog();
    },
  },
  beforeUnmount() {
    this.isAlive = false;
    this.activeAssistantRequestId = '';
  },
  methods: {
    openAssistantDialog() {
      this.assistantError = '';
      this.assistantStatus = this.assistantDraft
        ? 'Черновик уже готов. Можно вставлять или перезапустить агента.'
        : '';
      this.isAssistantDialogOpen = true;
      this.$refs.assistantDialog?.open();
    },
    dismissAssistantDialog() {
      this.$refs.assistantDialog?.close();
    },
    handleAssistantDialogClose() {
      this.isAssistantDialogOpen = false;
    },
    selectRequestedModel(model) {
      this.requestedModel = model;
    },
    selectAssistantExecutionMode(mode) {
      this.assistantExecutionMode = mode;
    },
    resetAssistantState() {
      this.assistantDraft = '';
      this.assistantError = '';
      this.assistantStatus = '';
      this.activeAssistantRequestId = '';
      this.activeTriggerMessageId = '';
      this.isAssistantRunning = false;
    },
    buildAssistantRequestId() {
      if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
      }

      return `dv-agent-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    },
    async runAssistant() {
      if (!this.canRunAssistant) {
        return;
      }

      const requestId = this.buildAssistantRequestId();

      this.assistantDraft = '';
      this.assistantError = '';
      this.assistantStatus = 'Агент уже пошел в бой и думает над черновиком.';
      if (this.isOperatorCommandMode) {
        this.assistantStatus =
          'Агент уже пошел в бой: проверяет кейс, и если всё сходится, сразу применит live-действие по брони.';
      }
      this.activeAssistantRequestId = requestId;
      this.activeTriggerMessageId = '';
      this.isAssistantRunning = true;

      try {
        const response = await MessageApi.create({
          conversationId: this.conversationId,
          message: '/bro',
          private: true,
          contentAttributes: {
            source_module: 'chatwoot_agent_launcher',
            assistant_request: {
              request_id: requestId,
              prompt: this.agentPrompt.trim(),
              requested_model: this.requestedModel,
              requested_from: 'reply_top_panel',
              execution_mode: this.assistantExecutionMode,
            },
          },
        });

        this.activeTriggerMessageId = String(response?.data?.id || '');

        const assistantNote = await this.pollAssistantNote(requestId);
        const draftText = this.extractDraftFromAssistantNote(assistantNote);

        if (!draftText) {
          throw new Error('Агент вернул заметку без текста черновика. Сюрреализм, конечно, жанр уважаемый, но нам нужен ответ.');
        }

        if (!this.isAlive || this.activeAssistantRequestId !== requestId) {
          return;
        }

        this.assistantDraft = draftText;
        this.assistantStatus = 'Черновик готов. Можно вставлять в ответ.';

        await this.cleanupLauncherNote(this.activeTriggerMessageId);
      } catch (error) {
        if (!this.isAlive || this.activeAssistantRequestId !== requestId) {
          return;
        }

        const errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          'Агент не ответил вовремя. Похоже, драматургия у него сегодня сильнее дисциплины.';

        this.assistantError = errorMessage;
        this.assistantStatus = '';
        useAlert(errorMessage);
      } finally {
        if (this.activeAssistantRequestId === requestId) {
          this.isAssistantRunning = false;
        }
      }
    },
    async pollAssistantNote(requestId) {
      const maxAttempts = 40;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        if (!this.isAlive || this.activeAssistantRequestId !== requestId) {
          return null;
        }

        const fromStore = this.findAssistantNote(
          this.selectedChatMessages,
          requestId,
          this.activeTriggerMessageId
        );
        if (fromStore) {
          return fromStore;
        }

        const { data } = await MessageApi.getPreviousMessages({
          conversationId: this.conversationId,
          after: this.activeTriggerMessageId || undefined,
        });
        const payload = Array.isArray(data?.payload) ? data.payload : [];
        const fromApi = this.findAssistantNote(
          payload,
          requestId,
          this.activeTriggerMessageId
        );

        if (fromApi) {
          return fromApi;
        }

        await this.sleep(1500);
      }

      throw new Error(
        'Не дождались ответа агента примерно за минуту. Он, видимо, ушел за истиной и не вернулся к дедлайну.'
      );
    },
    contentAttributesForMessage(message) {
      return (
        message?.content_attributes ||
        message?.contentAttributes ||
        {}
      );
    },
    findAssistantNote(messages, requestId, triggerMessageId = '') {
      return [...messages]
        .reverse()
        .find(message => {
          const contentAttributes = this.contentAttributesForMessage(message);
          const sourceModule = String(
            contentAttributes?.source_module || contentAttributes?.sourceModule || ''
          );
          const assistantRequestId = String(
            contentAttributes?.assistant_request_id ||
              contentAttributes?.assistantRequestId ||
              ''
          );
          const automationAction = String(
            contentAttributes?.automation_action ||
              contentAttributes?.automationAction ||
              ''
          );

          return (
            sourceModule === 'assistant_claude' &&
            (
              assistantRequestId === requestId ||
              (
                !assistantRequestId &&
                !!triggerMessageId &&
                automationAction === `assistant_claude:${triggerMessageId}`
              )
            )
          );
        });
    },
    extractDraftFromAssistantNote(message) {
      if (!message) {
        return '';
      }

      const contentAttributes = this.contentAttributesForMessage(message);
      const structuredDraft = String(
        contentAttributes?.assistant_draft_text ||
          contentAttributes?.assistantDraftText ||
          ''
      ).trim();

      if (structuredDraft) {
        return structuredDraft;
      }

      return this.extractLegacyDraftFromNote(String(message?.content || ''));
    },
    extractLegacyDraftFromNote(content) {
      const normalizedContent = String(content || '').trim();

      if (!normalizedContent) {
        return '';
      }

      const legacyDraftMatch = normalizedContent.match(
        /^\*\*DV Assistant draft\*\*\s*([\s\S]*?)\n---\n/m
      );

      if (legacyDraftMatch?.[1]) {
        return legacyDraftMatch[1].trim();
      }

      return normalizedContent;
    },
    async cleanupLauncherNote(messageId) {
      if (!messageId) {
        return;
      }

      try {
        await MessageApi.delete(this.conversationId, messageId);
      } catch (error) {
        // If cleanup fails, we keep going. The technical note is ugly, not fatal.
      }
    },
    insertAssistantDraft() {
      if (!this.assistantDraft) {
        return;
      }

      const content = prepareAssistantDraftForRichEditor(this.assistantDraft);
      if (!content) {
        return;
      }

      emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, content);
      this.dismissAssistantDialog();
    },
    sleep(ms) {
      return new Promise(resolve => {
        window.setTimeout(resolve, ms);
      });
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-2 h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      @toggle-mode="handleModeToggle"
    />
    <div class="flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div v-if="captainTasksEnabled" class="flex items-center gap-2">
      <NextButton
        ghost
        sm
        icon="i-woot-captain"
        :disabled="disabled || !conversationId || isAssistantRunning"
        :class="assistantButtonClass"
        title="DV Agent"
        @click="openAssistantDialog"
      />
      <div class="relative">
        <NextButton
          ghost
          :disabled="disabled || isEditorDisabled"
          :class="{
            'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
            'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
          }"
          sm
          icon="i-ph-sparkle-fill"
          @click="toggleCopilotMenu"
        />
        <CopilotMenuBar
          v-if="showCopilotMenu"
          v-on-click-outside="handleClickOutside"
          :has-selection="false"
          :editor-content="editorContent"
          :conversation-id="conversationId"
          class="ltr:right-0 rtl:left-0 bottom-full mb-2"
          @execute-copilot-action="handleCopilotAction"
        />
      </div>
      <NextButton
        ghost
        class="text-n-slate-11"
        sm
        icon="i-lucide-maximize-2"
        @click="$emit('togglePopout')"
      />
    </div>
  </div>

  <Dialog
    ref="assistantDialog"
    width="2xl"
    position="top"
    scroll-content
    title="DV Agent"
    description="Оставьте подсказку для ответа или переключите режим в настоящий боевой, если надо не только подумать, но и сразу применить live-действие по брони."
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleAssistantDialogClose"
  >
    <div
      class="flex min-h-0 flex-col gap-5"
    >
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Подсказка оператора
        </label>
        <textarea
          v-model="agentPrompt"
          rows="4"
          class="w-full px-4 py-3 text-sm border rounded-xl bg-n-alpha-2 border-n-slate-5 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-blue-8/40"
          placeholder="Например: ответь короче, увереннее и предложи следующий шаг. Если оставить пустым, агент сам решит, как лучше."
        />
      </div>

      <div class="flex flex-col gap-2">
        <div class="text-sm font-medium text-n-slate-12">Режим</div>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
          <button
            v-for="option in assistantExecutionModeOptions"
            :key="option.value"
            type="button"
            class="flex flex-col items-start gap-1 rounded-xl border px-4 py-3 text-left transition-colors"
            :class="
              assistantExecutionMode === option.value
                ? 'border-n-blue-8 bg-n-blue-3 text-n-blue-11'
                : 'border-n-slate-4 bg-n-alpha-2 text-n-slate-12 hover:border-n-slate-6'
            "
            @click="selectAssistantExecutionMode(option.value)"
          >
            <span class="text-sm font-medium">{{ option.label }}</span>
            <span class="text-xs leading-5 opacity-80">{{ option.hint }}</span>
          </button>
        </div>
        <p class="text-xs text-n-slate-10 mb-0">
          {{ selectedExecutionModeHint }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <div class="text-sm font-medium text-n-slate-12">Модель</div>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
          <button
            v-for="option in assistantModelOptions"
            :key="option.value"
            type="button"
            class="flex flex-col items-start gap-1 rounded-xl border px-4 py-3 text-left transition-colors"
            :class="
              requestedModel === option.value
                ? 'border-n-blue-8 bg-n-blue-3 text-n-blue-11'
                : 'border-n-slate-4 bg-n-alpha-2 text-n-slate-12 hover:border-n-slate-6'
            "
            @click="selectRequestedModel(option.value)"
          >
            <span class="text-sm font-medium">{{ option.label }}</span>
            <span class="text-xs leading-5 opacity-80">{{ option.hint }}</span>
          </button>
        </div>
        <p class="text-xs text-n-slate-10 mb-0">
          {{ selectedModelHint }}
        </p>
      </div>

      <div
        v-if="assistantStatus"
        class="rounded-xl border border-n-blue-5 bg-n-blue-3 px-4 py-3 text-sm text-n-blue-11"
      >
        {{ assistantStatus }}
      </div>

      <div
        v-if="assistantError"
        class="rounded-xl border border-n-ruby-5 bg-n-ruby-2 px-4 py-3 text-sm text-n-ruby-11"
      >
        {{ assistantError }}
      </div>

      <div
        v-if="assistantDraft"
        class="flex min-h-0 flex-col gap-2 rounded-xl border border-n-slate-4 bg-n-alpha-2 px-4 py-4"
      >
        <div class="text-sm font-medium text-n-slate-12">Черновик ответа</div>
        <div
          class="break-words text-sm leading-6 text-n-slate-12 whitespace-pre-wrap"
        >
          {{ assistantDraft }}
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex w-full shrink-0 flex-wrap items-center justify-end gap-3">
        <NextButton
          label="Закрыть"
          color="slate"
          variant="faded"
          type="button"
          @click="dismissAssistantDialog"
        />
        <NextButton
          v-if="assistantDraft"
          label="Вставить в ответ"
          color="slate"
          variant="faded"
          type="button"
          :disabled="!canInsertAssistantDraft"
          @click="insertAssistantDraft"
        />
        <NextButton
          :label="assistantRunButtonLabel"
          color="blue"
          type="button"
          :disabled="!canRunAssistant"
          :is-loading="isAssistantRunning"
          @click="runAssistant"
        />
      </div>
    </template>
  </Dialog>
</template>
